import {api} from './api';
import {mapJobData} from "./jobApi";
import {mapCompanyData} from "./companyApi";

export const publicApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getPublicJob: builder.query({
            query: (id: string) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
        query GetCustomJob($id: ID!) {
          getPublicJob(id: $id)
        }
      `,
                    variables: {id},
                },
            }),
            transformResponse: (response: any) => mapJobData(response.data?.getPublicJob),
        }),
        getPublicJobs: builder.query({
            query: ({skip = 0, companyId, searchText}) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query GetPublicJobs($skip: Int!, $companyId: String, $searchText: String) {
              getPublicJobs(skip: $skip, companyId: $companyId, searchText: $searchText)
            }
          `, variables: {skip, companyId, searchText},
                },
            }),
            transformResponse: (response) => {
                return {
                    totalCount: response.data.getPublicJobs.totalCount,
                    jobs: (response.data.getPublicJobs.jobs || []).map(mapJobData)
                }
            },
        }),
        //Gets n many open jobs
        getFeaturedJobs: builder.query({
            query: () => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query Jobs {
               getFeaturedJobs
            }
          `
                },
            }),
            transformResponse: (response) => (response.data.getFeaturedJobs || []).map(mapJobData),
        }),
        getPublicCompanies: builder.query({
            query: ({skip = 0, searchText}) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query GetCustomCompanies($skip: Int!, $searchText: String) {
              getPublicCompanies(skip: $skip, searchText: $searchText)
            }
          `,
                    variables: {skip, searchText},
                },
            }),
            transformResponse: (response: any) => {
                return {
                    totalCount: response.data.getPublicCompanies.totalCount,
                    companies: (response.data.getPublicCompanies.companies || []).map(mapCompanyData)
                }
            },
            providesTags: ['Company'],
        }),
        //Gets n many verified companies
        getFeaturedCompanies: builder.query({
            query: () => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query Companies {
                getFeaturedCompanies
            }
          `
                },
            }),
            transformResponse: (response: any) => (response.data?.getFeaturedCompanies || []).map(mapCompanyData),
        }),
        getMarketplaceCategories: builder.query({
            query: ({homepageOnly = false}: {homepageOnly?: boolean} = {}) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query MarketplaceCategories($homepageOnly: Boolean) {
                getMarketplaceCategories(homepageOnly: $homepageOnly)
            }
          `,
                    variables: {homepageOnly},
                },
            }),
            transformResponse: (response: any) => response.data?.getMarketplaceCategories || [],
        }),
        getMarketplaceListings: builder.query({
            query: ({categoryId, searchText, supplier, country}: {
                categoryId?: string;
                searchText?: string;
                supplier?: string;
                country?: string;
            }) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query MarketplaceListings($categoryId: String, $searchText: String, $supplier: String, $country: String) {
                getMarketplaceListings(categoryId: $categoryId, searchText: $searchText, supplier: $supplier, country: $country)
            }
          `,
                    variables: {categoryId, searchText, supplier, country},
                },
            }),
            transformResponse: (response: any) => response.data?.getMarketplaceListings || [],
        }),
        getMarketplaceSuppliers: builder.query({
            query: () => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
            query MarketplaceSuppliers {
                getMarketplaceSuppliers
            }
          `,
                },
            }),
            transformResponse: (response: any) => response.data?.getMarketplaceSuppliers || [],
        }),
        getPublicCompanyDetail: builder.query({
            query: (id: string) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
        query GetCustomCompanyDetail($id: ID!) {
          getPublicCompanyDetail(id: $id)
        }
      `,
                    variables: {id},
                },
            }),
            transformResponse: (response: any) => mapCompanyData(response.data.getPublicCompanyDetail),
            providesTags: (result, error, id) => [{type: 'Company', id}],
        }),
        getPublicUserTypes: builder.query({
            query: () => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
             query Query {
                getPublicUserTypes
             }
      `,
                },
            }),
            transformResponse: (response: { data: { getPublicUserTypes: { id: string, type: string }[] } }) => {
                return (response.data?.getPublicUserTypes || []).map((item) => ({
                    id: item.id,
                    type: item.type,
                }));
            },
        }),
        saveContact: builder.mutation<{ id: string }, {
            full_name: string;
            email: string;
            message: string;
            user_type_id?: string
        }>({
            query: (contactInfo) => ({
                url: '',
                method: 'POST',
                body: {
                    query: `
                        mutation CreateContact($data: ContactCreateInput!) {
                          createContact(data: $data) {
                            id
                          }
                        }
                      `,
                    variables: {
                        data: {
                            full_name: contactInfo.full_name,
                            email: contactInfo.email,
                            message: contactInfo.message,
                            ...(contactInfo.user_type_id && {
                                user_type: {
                                    connect: {
                                        id: contactInfo.user_type_id,
                                    },
                                },
                            }),
                        },
                    },
                },
            }),
            transformResponse: (response: { data: { createContact: { id: string } } }) =>
                response.data.createContact,
        })
    }),
});

export const {
    useGetPublicCompaniesQuery,
    useGetPublicJobsQuery,
    useGetFeaturedJobsQuery,
    useGetFeaturedCompaniesQuery,
    useGetMarketplaceCategoriesQuery,
    useGetMarketplaceListingsQuery,
    useGetMarketplaceSuppliersQuery,
    useGetPublicJobQuery,
    useGetPublicCompanyDetailQuery,
    useGetPublicUserTypesQuery,
    useSaveContactMutation
} = publicApi;
